import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { ArrowForward, Business, Lock, Verified } from '@mui/icons-material';
import api from '../services/api';
import LoginModal from '../modals/LoginModal';
import { isSafeInternalReturnPath } from '../components/SharedLinkGate';

export default function SharedVendorPreview() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [vendor, setVendor] = useState(null);
  const [propertyCount, setPropertyCount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loginOpen, setLoginOpen] = useState(false);
  const [initialTab, setInitialTab] = useState(0);

  const returnTo = isSafeInternalReturnPath(location.pathname) ? location.pathname : '/investor';

  useEffect(() => {
    let cancelled = false;

    async function fetchPreview() {
      try {
        setLoading(true);
        setError(null);
        const vendorResponse = await api.vendors.getById(id);
        if (cancelled) return;
        setVendor(vendorResponse.data);

        try {
          const propertiesResponse = await api.properties.getPublicByVendor(id);
          if (!cancelled) setPropertyCount(propertiesResponse.data?.length ?? null);
        } catch (propertyError) {
          if (!cancelled) setPropertyCount(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError('This storefront is unavailable or requires an invitation.');
          setVendor(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchPreview();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const openAuth = (tab) => {
    setInitialTab(tab);
    setLoginOpen(true);
  };

  const handleAuthenticated = () => {
    navigate(returnTo, { replace: true });
  };

  const signupContext = {
    sourceType: 'vendor_storefront',
    vendorId: id,
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Container maxWidth="md" sx={{ py: { xs: 4, md: 8 } }}>
        <Paper elevation={2} sx={{ p: { xs: 3, md: 5 }, borderRadius: 2 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress sx={{ color: '#008080' }} />
            </Box>
          ) : (
            <Stack spacing={3}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                <Avatar
                  src={vendor?.profilePhoto || vendor?.profileImage}
                  sx={{ width: 88, height: 88, bgcolor: '#008080' }}
                >
                  <Business fontSize="large" />
                </Avatar>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                    <Typography variant="h4" component="h1" sx={{ fontWeight: 800, color: '#008080' }}>
                      {vendor?.name || 'Private OffMarkets Storefront'}
                    </Typography>
                    {vendor?.verified && <Verified sx={{ color: '#008080' }} />}
                  </Stack>
                  <Typography variant="h6" color="text.secondary">
                    {[vendor?.title, vendor?.company].filter(Boolean).join(' at ') || 'Verified real estate professional'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {vendor?.location || 'Off-market opportunities available by invitation'}
                  </Typography>
                </Box>
              </Stack>

              {error && (
                <Typography color="text.secondary">
                  {error}
                </Typography>
              )}

              <Box sx={{ p: 2.5, border: '1px solid #e6eeee', borderRadius: 1, bgcolor: 'rgba(0, 128, 128, 0.04)' }}>
                <Stack spacing={1.5}>
                  <Chip
                    icon={<Lock />}
                    label="Invitation preview"
                    sx={{ alignSelf: 'flex-start', bgcolor: '#008080', color: 'white', '& .MuiChip-icon': { color: 'white' } }}
                  />
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    Join to view this vendor's private opportunities.
                  </Typography>
                  <Typography color="text.secondary">
                    {propertyCount == null
                      ? 'This storefront includes curated off-market opportunities shared with approved investor accounts.'
                      : `${propertyCount} off-market ${propertyCount === 1 ? 'opportunity is' : 'opportunities are'} available after sign in.`}
                  </Typography>
                </Stack>
              </Box>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <Button
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForward />}
                  onClick={() => openAuth(1)}
                  sx={{ bgcolor: '#008080', '&:hover': { bgcolor: '#005252' } }}
                >
                  Create free investor account
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => openAuth(0)}
                  sx={{ borderColor: '#008080', color: '#008080' }}
                >
                  Sign in
                </Button>
              </Stack>
            </Stack>
          )}
        </Paper>
      </Container>

      <LoginModal
        open={loginOpen}
        onClose={() => setLoginOpen(false)}
        defaultUserType="investor"
        initialTab={initialTab}
        lockUserType
        signupContext={signupContext}
        onAuthenticated={handleAuthenticated}
      />
    </Box>
  );
}
